/**
 * Quiz session state machine.
 *
 * Two modes:
 *
 *   **Procedural**: caller supplies `generate`. Every `next()` produces
 *   a fresh question via `generate()`. Quiz never "completes" — the
 *   user can keep going indefinitely.
 *
 *   **Bank**: caller supplies `bank` (a finite array of pre-authored
 *   questions). The hook shuffles the bank once, walks through it
 *   one question at a time, and sets `isComplete = true` after the
 *   user answers the last one + clicks `next()`. `restart()` reshuffles
 *   and resets to position 0 (stats stay; restart only resets bank
 *   position).
 *
 * Stats persist via localStorage (`persistedStats` module) so they
 * survive reloads. Day rollover for "today" counters is automatic.
 */

import { useCallback, useEffect, useState } from "react";

import { recordQuizAttempt } from "@/shared/api";
import {
  INITIAL_SESSION_STATS,
  type SessionStats,
  advanceStats,
  shuffle,
} from "@/features/quiz/lib/common";
import { loadStats, saveStats } from "@/features/quiz/lib/persistedStats";

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

interface BaseOpts<TQuestion, TAnswer extends string | number> {
  quizType: string;
  getCorrectAnswer: (question: TQuestion) => TAnswer;
  getQuestionPayload: (question: TQuestion) => Record<string, unknown>;
}

interface ProceduralOpts<TQuestion, TAnswer extends string | number>
  extends BaseOpts<TQuestion, TAnswer> {
  /** Procedural source — called once on mount and on every `next()`. */
  generate: () => TQuestion;
  bank?: never;
}

interface BankOpts<TQuestion, TAnswer extends string | number>
  extends BaseOpts<TQuestion, TAnswer> {
  /** Finite question bank. The hook shuffles + walks through it. */
  bank: ReadonlyArray<TQuestion>;
  generate?: never;
}

export type UseQuizSessionOptions<TQuestion, TAnswer extends string | number> =
  | ProceduralOpts<TQuestion, TAnswer>
  | BankOpts<TQuestion, TAnswer>;

export interface QuizSessionState<TQuestion, TAnswer extends string | number> {
  question: TQuestion;
  revealedAnswer: TAnswer | null;
  isCorrect: boolean;
  correctAnswer: TAnswer;
  stats: SessionStats;
  submit: (answer: TAnswer) => void;
  /** Advance to the next question. In bank mode, may instead trigger
   *  `isComplete` if we just answered the last question. */
  next: () => void;
  // --- Bank-mode-only fields (undefined in procedural mode) ---
  /** 1-based position within the bank (e.g. 3 of 12). */
  bankPosition?: number;
  /** Total questions in the bank. */
  bankSize?: number;
  /** True after the user answered the last question + clicked next. */
  isComplete: boolean;
  /** Reset bank position to 0 with a fresh shuffle. Stats stay. */
  restart: () => void;
}

// ----------------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------------

export function useQuizSession<TQuestion, TAnswer extends string | number>(
  opts: UseQuizSessionOptions<TQuestion, TAnswer>,
): QuizSessionState<TQuestion, TAnswer> {
  const { quizType, getCorrectAnswer, getQuestionPayload } = opts;
  const bank = "bank" in opts ? opts.bank : undefined;
  const generate = "generate" in opts ? opts.generate : undefined;

  // Bank order: shuffled once on mount. Reshuffled on restart().
  const [bankOrder, setBankOrder] = useState<number[]>(() =>
    bank ? shuffle(bank.map((_, i) => i)) : [],
  );
  const [bankIndex, setBankIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const [question, setQuestion] = useState<TQuestion>(() => {
    if (bank) return bank[bankOrder[0]!]!;
    return generate!();
  });
  const [revealedAnswer, setRevealedAnswer] = useState<TAnswer | null>(null);
  const [stats, setStats] = useState<SessionStats>(() => loadStats(quizType));

  // Persist stats whenever they change. The initial useState load reads
  // from localStorage; this effect writes back on every mutation.
  useEffect(() => {
    // Don't write on the very first render if we just loaded — avoids
    // a useless round-trip. Skip when stats match the initial empty
    // state (means we haven't done anything yet).
    if (stats === INITIAL_SESSION_STATS) return;
    saveStats(quizType, stats);
  }, [quizType, stats]);

  const correctAnswer = getCorrectAnswer(question);

  const submit = useCallback(
    (answer: TAnswer) => {
      if (revealedAnswer !== null) return;
      const correct = getCorrectAnswer(question);
      const isCorrect = answer === correct;
      setRevealedAnswer(answer);
      setStats((prev) => advanceStats(prev, isCorrect));

      recordQuizAttempt({
        quizType,
        question: getQuestionPayload(question),
        userAnswer: String(answer),
        correctAnswer: String(correct),
      }).catch(() => {
        // Fire-and-forget. Local stats stay accurate even on backend miss.
      });
    },
    [revealedAnswer, question, quizType, getCorrectAnswer, getQuestionPayload],
  );

  const next = useCallback(() => {
    if (bank) {
      const nextIdx = bankIndex + 1;
      if (nextIdx >= bank.length) {
        // We just answered the last question; flip into complete mode.
        // The route renders the completion panel instead of the next
        // question.
        setIsComplete(true);
        return;
      }
      setBankIndex(nextIdx);
      setQuestion(bank[bankOrder[nextIdx]!]!);
      setRevealedAnswer(null);
    } else {
      setQuestion(generate!());
      setRevealedAnswer(null);
    }
  }, [bank, bankIndex, bankOrder, generate]);

  const restart = useCallback(() => {
    if (!bank) return;
    const fresh = shuffle(bank.map((_, i) => i));
    setBankOrder(fresh);
    setBankIndex(0);
    setQuestion(bank[fresh[0]!]!);
    setRevealedAnswer(null);
    setIsComplete(false);
  }, [bank]);

  return {
    question,
    revealedAnswer,
    isCorrect: revealedAnswer !== null && revealedAnswer === correctAnswer,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition: bank ? bankIndex + 1 : undefined,
    bankSize: bank ? bank.length : undefined,
    isComplete,
    restart,
  };
}
